import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Card, CardContent } from '@components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@components/ui/table';
import { Spinner } from '../../../components/spinner';
import { BackfillableApp, BackfillModal } from './backfill-modal';
import { useUserFitnessAppsStore, UserFitnessAppSupportDto } from './store';

const formatDateTime = (value?: string) =>
    value ? new Date(value).toLocaleString() : '-';

const formatAppName = (name: string) =>
    name
        .split(/[_\s]+/)
        .filter(Boolean)
        .map((word) => word[0].toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');

const BACKFILL_SUPPORTED_APPS = [
    'GARMIN',
    'HUAWEI_HEALTH',
    'STRAVA',
    'SUUNTO',
    'ZEPP'
];

export const UserFitnessApps = observer(() => {
    const { connections, triggerBackfill, refresh, userId } =
        useUserFitnessAppsStore();
    const [backfillApp, setBackfillApp] = useState<BackfillableApp | null>(
        null
    );

    return (
        <div>
            <h2 className="mb-4 text-lg font-semibold tracking-tight">
                Connected Fitness Apps
            </h2>

            {connections.match({
                loading: () => (
                    <div className="flex justify-center py-12">
                        <Spinner />
                    </div>
                ),
                error: (error) => (
                    <Alert variant="destructive">
                        <AlertTitle>Error loading fitness apps</AlertTitle>
                        <AlertDescription>{error.message}</AlertDescription>
                    </Alert>
                ),
                empty: () => (
                    <Alert variant="info">
                        <AlertDescription>
                            This user has no connected fitness apps.
                        </AlertDescription>
                    </Alert>
                ),
                fetched: (apps: UserFitnessAppSupportDto[]) => (
                    <Card>
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>App</TableHead>
                                        <TableHead>External user id</TableHead>
                                        <TableHead>Token expires</TableHead>
                                        <TableHead>Connected</TableHead>
                                        <TableHead>Disconnected</TableHead>
                                        <TableHead className="text-right">
                                            Actions
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {apps.map((app) => (
                                        <TableRow key={app.id}>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <span>
                                                        {formatAppName(
                                                            app.name
                                                        )}
                                                    </span>
                                                    {app.deletedDate ? (
                                                        <Badge variant="muted">
                                                            Disconnected
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="success">
                                                            Connected
                                                        </Badge>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {app.externalUserId || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {formatDateTime(
                                                    app.tokenExpiresAt
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {formatDateTime(
                                                    app.createdDate
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {formatDateTime(
                                                    app.deletedDate
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {BACKFILL_SUPPORTED_APPS.includes(
                                                    app.fitnessApp
                                                ) &&
                                                    !app.deletedDate && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() =>
                                                                setBackfillApp({
                                                                    fitnessApp:
                                                                        app.fitnessApp,
                                                                    name: app.name
                                                                })
                                                            }
                                                        >
                                                            Backfill
                                                        </Button>
                                                    )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                )
            })}

            <BackfillModal
                open={backfillApp !== null}
                onClose={() => setBackfillApp(null)}
                userId={userId}
                app={backfillApp}
                onBackfill={async (request) => {
                    await triggerBackfill(request);
                    refresh();
                }}
            />
        </div>
    );
});

export default UserFitnessApps;
